import { useState, useCallback, useEffect } from 'react';
import { BackHandler, Image, StyleSheet, TouchableOpacity, useWindowDimensions } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useRouter } from 'expo-router';

import { useColorScheme } from '@/hooks/use-color-scheme';
import { useThemeColor } from '@/hooks/use-theme-color';
import { useFabCoachMark } from '@/hooks/use-fab-coach-mark';
import { useActiveRoutePlan } from '@/hooks/use-active-route-plan';
import { useChatStore } from '@/lib/chat/useChatStore';
import { useAISuggestionsStore } from '@/lib/suggestions/useAISuggestionsStore';
import { ChatModal } from './ChatModal';
import { ChatFABMenu } from './ChatFABMenu';
import { FABCoachMark } from './FABCoachMark';
import { AddByPhotoModal } from './photo/AddByPhotoModal';
import { AISuggestionsModal } from '@/components/suggestions/AISuggestionsModal';
import { SuggestionsLoadingBubble } from '@/components/suggestions/SuggestionsLoadingBubble';

/**
 * ChatFAB Component
 * Floating Action Button for accessing AI chat and AI suggestions.
 * Shows a loading bubble when suggestions are generating in the background.
 */
export function ChatFAB() {
  const { isModalVisible, openModal, closeModal } = useChatStore();
  const pendingTodoNavigation = useChatStore((state) => state.pendingTodoNavigation);
  const clearTodoNavigation = useChatStore((state) => state.clearTodoNavigation);
  const router = useRouter();
  const insets = useSafeAreaInsets();

  // Navigate to todo tab after modal closes
  useEffect(() => {
    if (!isModalVisible && pendingTodoNavigation) {
      clearTodoNavigation();
      router.push('/(tabs)/todo');
    }
  }, [isModalVisible, pendingTodoNavigation, clearTodoNavigation, router]);

  const { width: screenWidth } = useWindowDimensions();
  const colorScheme = useColorScheme();
  const { isCoachMarkVisible, dismissCoachMark } = useFabCoachMark();
  const backgroundColor = useThemeColor({}, 'background');
  const chatIconSource = colorScheme === 'dark'
    ? require('@/assets/images/aichat-dark.png')
    : require('@/assets/images/aichat.png');

  const [isMenuVisible, setIsMenuVisible] = useState(false);
  const [isSuggestionsVisible, setIsSuggestionsVisible] = useState(false);
  const [isPhotoVisible, setIsPhotoVisible] = useState(false);

  const { plan: activeRoutePlan, progress: activeRouteProgress, refresh: refreshRoutePlan } = useActiveRoutePlan();

  // Refresh the active-plan state whenever the menu opens so the
  // "Route Planner" / "Today's Route (n of m)" label is current.
  useEffect(() => {
    if (isMenuVisible) {
      refreshRoutePlan();
    }
  }, [isMenuVisible, refreshRoutePlan]);

  const phase = useAISuggestionsStore((state) => state.phase);
  const showBubble = phase === 'background-loading' || phase === 'results' || phase === 'error';
  const isReady = phase === 'results' || phase === 'error';

  const bottomPosition = Math.max(insets.bottom, 12) + 12;
  const fabLeft = screenWidth / 2 - 30;

  const handleFabPress = useCallback(() => {
    dismissCoachMark();
    setIsMenuVisible((prev) => !prev);
  }, [dismissCoachMark]);

  const handleAskFim = useCallback(() => {
    setIsMenuVisible(false);
    openModal();
  }, [openModal]);

  const handleAISuggestions = useCallback(() => {
    setIsMenuVisible(false);
    setIsSuggestionsVisible(true);
  }, []);

  const handleAddByPhoto = useCallback(() => {
    setIsMenuVisible(false);
    setIsPhotoVisible(true);
  }, []);

  const handleRoutePlanner = useCallback(() => {
    setIsMenuVisible(false);
    router.push(activeRoutePlan ? '/route-planner/map' : '/route-planner');
  }, [router, activeRoutePlan]);

  const handleCloseMenu = useCallback(() => {
    setIsMenuVisible(false);
  }, []);

  // Dismiss FAB menu on Android hardware back button
  useEffect(() => {
    if (!isMenuVisible) return;

    const subscription = BackHandler.addEventListener('hardwareBackPress', () => {
      handleCloseMenu();
      return true;
    });

    return () => subscription.remove();
  }, [isMenuVisible, handleCloseMenu]);

  const handleCloseSuggestions = useCallback(() => {
    setIsSuggestionsVisible(false);
  }, []);

  const handleBubblePress = useCallback(() => {
    setIsSuggestionsVisible(true);
  }, []);

  return (
    <>
      {showBubble && !isSuggestionsVisible && (
        <SuggestionsLoadingBubble
          isReady={isReady}
          onPress={handleBubblePress}
          bottomOffset={bottomPosition}
        />
      )}

      <FABCoachMark
        visible={isCoachMarkVisible && !isMenuVisible}
        bottomOffset={bottomPosition + 60}
        onDismiss={dismissCoachMark}
      />

      <TouchableOpacity
        style={[
          styles.chatFab,
          {
            backgroundColor: backgroundColor,
            borderColor: colorScheme === 'dark' ? '#F0A050' : '#000000',
            borderWidth: colorScheme === 'dark' ? 1.5 : 0.5,
            bottom: bottomPosition,
            left: fabLeft,
          },
        ]}
        onPress={handleFabPress}
        activeOpacity={0.8}
        accessibilityLabel="Ask Fim - Open AI menu"
        accessibilityRole="button"
      >
        <Image
          source={chatIconSource}
          style={styles.iconImage}
        />
      </TouchableOpacity>

      <ChatFABMenu
        visible={isMenuVisible}
        onClose={handleCloseMenu}
        onAskFim={handleAskFim}
        onAISuggestions={handleAISuggestions}
        onAddByPhoto={handleAddByPhoto}
        onRoutePlanner={handleRoutePlanner}
        activeRouteProgress={activeRoutePlan ? activeRouteProgress : null}
        anchorBottom={bottomPosition}
      />

      <ChatModal
        visible={isModalVisible}
        onDismiss={closeModal}
      />

      <AISuggestionsModal
        visible={isSuggestionsVisible}
        onClose={handleCloseSuggestions}
      />

      <AddByPhotoModal
        visible={isPhotoVisible}
        onDismiss={() => setIsPhotoVisible(false)}
      />
    </>
  );
}

const styles = StyleSheet.create({
  chatFab: {
    position: 'absolute',
    width: 60,
    height: 60,
    borderRadius: 30,
    alignItems: 'center',
    justifyContent: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.15,
    shadowRadius: 3,
    elevation: 3,
    zIndex: 100,
  },
  iconImage: {
    width: 44,
    height: 44,
    borderRadius: 22,
  },
});
